import axios from "axios";
import { toast } from "sonner";
import { getAuthToken, setAuthToken } from "@/lib/impersonation";

export { getAuthToken, setAuthToken };

const resolveApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  if (import.meta.env.MODE === "test") {
    return "http://localhost:8082/api/v1";
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/v1`;
  }

  return "http://localhost:8082/api/v1";
};

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Absolute URL for an API path that a browser will fetch directly, such as a
 * media <video>/<audio> src. Relative "/api/v1/..." paths only work when the
 * hosting server proxies /api, so resolve them against the configured API base
 * instead of the current origin.
 */
export const apiUrl = (path: string): string => {
  const base = (apiClient.defaults.baseURL || "").replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
};

apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // The client-wide JSON default makes axios serialize FormData to JSON,
    // which strips the file. Clearing it lets the browser set the multipart
    // boundary itself.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor to handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const skipGlobal = (error.config as any)?.skipGlobalErrorHandler;
    if (skipGlobal) {
      return Promise.reject(error);
    }

    const currentPath = window.location.pathname;
    const isAuthPage = currentPath.startsWith("/auth");
    // Onboarding is authenticated — a 401 there must surface, not be swallowed.
    const isPublicPage = currentPath === "/" || currentPath === "/contact";

    // Check if user is in demo mode - don't redirect or show auth errors
    const isDemoMode = localStorage.getItem("taskpart_demo_user") !== null;

    // Global error message extraction
    const errorMessage =
      error.response?.data?.message || error.message || "An unexpected error occurred";

    if (error.response?.status === 401) {
      if (isDemoMode) {
        // Don't redirect or show error for demo mode
        return Promise.reject(error);
      }
      if (!isAuthPage && !isPublicPage) {
        // Dispatch custom event so the root component can listen and navigate
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      } else if (isAuthPage) {
        // Special case for login/register failures
        toast.error("Authentication Failed", {
          description: errorMessage,
        });
      }
    } else if (error.response?.status === 403) {
      // 403 covers more than role denial. TierMiddleware rejects with the
      // UPGRADE_REQUIRED code while RoleMiddleware sends no code at all, and
      // both already carry a message written for the person reading it. Showing
      // a fixed "no permission" string here hid the membership upgrade prompt
      // behind a permission error, so the backend reason is passed through.
      const errorField = error.response?.data?.error;
      const code = typeof errorField === "string" ? errorField : undefined;

      if (code === "UPGRADE_REQUIRED") {
        toast.error("Upgrade Required", {
          description: errorMessage,
        });
      } else {
        toast.error("Access Denied", {
          description: errorMessage,
        });
      }
    } else if (error.response?.status === 429) {
      toast.error("Too Many Requests", {
        description: "Please slow down and try again later.",
      });
    } else if (error.response?.status >= 500) {
      toast.error("Server Error", {
        description: "Something went wrong on our end. Our team has been notified.",
      });
    } else if (!axios.isCancel(error)) {
      // General error fallback
      toast.error("Error", {
        description: errorMessage,
      });
    }

    return Promise.reject(error);
  },
);

export const getFileUrl = (path: string | undefined) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;

  const baseUrl = apiClient.defaults.baseURL || "";
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

  // Backend returns "uploads/..." path, so we want [baseURL]/uploads/...
  return `${cleanBaseUrl}/${path}`;
};

export default apiClient;
